import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import ReviewPanel from "./ReviewPanel";
import type {
  CourseEvaluatorReviewDecision,
  CourseEvaluatorReviewMockData,
  CourseEvaluatorReviewSidebarGroupMock,
  CourseEvaluatorReviewSidebarItemMock,
} from "./mockData/courseEvaluatorReview.mockData";
import type { ActivityDetailMock } from "./types/reviewTypes";

interface DecisionEntry {
  decision: CourseEvaluatorReviewDecision;
}

interface UndoEntry {
  itemId: string;
  previous: DecisionEntry;
}

interface CourseEvaluatorReviewContainerProps {
  review: CourseEvaluatorReviewMockData;
  activityDetails: Record<string, ActivityDetailMock>;
  onExitReview: () => void;
}

function CourseEvaluatorReviewContainer({
  review,
  activityDetails,
  onExitReview,
}: CourseEvaluatorReviewContainerProps) {
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [filterMode, setFilterMode] = useState<"all" | "undecided">("all");
  const [collapsedSectionIds, setCollapsedSectionIds] = useState<Set<string>>(
    () => new Set(review.sidebarGroups.filter((group) => !group.defaultExpanded).map((group) => group.id))
  );
  const [decisions, setDecisions] = useState<Map<string, DecisionEntry>>(() => new Map());
  const [undoEntry, setUndoEntry] = useState<UndoEntry | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Ref to track latest decisions for non-blocking auto-advance
  const decisionsRef = useRef(decisions);
  decisionsRef.current = decisions;

  // Auto-save timer ref
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
    };
  }, []);

  const allItems = useMemo(() => review.sidebarGroups.flatMap((group) => group.items), [review.sidebarGroups]);

  const orderedIds = useMemo(() => allItems.map((item) => item.id), [allItems]);

  const liveSidebarGroups = useMemo<CourseEvaluatorReviewSidebarGroupMock[]>(() => {
    return review.sidebarGroups
      .map((group) => {
        const decided = group.items.filter((item) => decisions.get(item.id)?.decision !== undefined).length;
        const items =
          filterMode === "undecided"
            ? group.items.filter((item) => decisions.get(item.id)?.decision === undefined)
            : group.items;

        return { ...group, decided, items };
      })
      .filter((group) => filterMode === "all" || group.items.length > 0);
  }, [review.sidebarGroups, filterMode, decisions]);

  const selectedSidebarItem = useMemo<CourseEvaluatorReviewSidebarItemMock | null>(() => {
    if (!selectedItemId) return null;
    return allItems.find((item) => item.id === selectedItemId) || null;
  }, [allItems, selectedItemId]);

  const getDecision = useCallback(
    (id: string): CourseEvaluatorReviewDecision => decisions.get(id)?.decision ?? null,
    [decisions]
  );

  const selectedDecision = selectedItemId ? getDecision(selectedItemId) : null;

  // --- Stats ---
  const stats = useMemo(() => {
    let accepted = 0;
    let rejected = 0;
    allItems.forEach((item) => {
      const d = decisions.get(item.id)?.decision;
      if (d === "accepted" || d === "edited") accepted++;
      if (d === "rejected") rejected++;
    });
    const decided = accepted + rejected;
    return { decided, accepted, rejected, total: allItems.length };
  }, [allItems, decisions]);

  const reviewedProgress = stats.total > 0 ? (stats.decided / stats.total) * 100 : 0;

  // Override review data with live stats + filtered sidebar
  const liveReview = useMemo<CourseEvaluatorReviewMockData>(
    () => ({
      ...review,
      reviewedCount: stats.decided,
      queuedCount: stats.accepted,
      skippedCount: stats.rejected,
      sidebarGroups: liveSidebarGroups,
    }),
    [review, stats, liveSidebarGroups]
  );

  // --- Non-blocking auto-advance ---
  const advanceToNextUndecided = useCallback(
    (fromId: string) => {
      // Use requestAnimationFrame to keep the UI responsive
      requestAnimationFrame(() => {
        if (!isMountedRef.current) return;

        const currentDecisions = decisionsRef.current;
        const currentIdx = orderedIds.indexOf(fromId);

        // Search forward first
        for (let i = currentIdx + 1; i < orderedIds.length; i++) {
          const id = orderedIds[i];
          if (!currentDecisions.get(id)?.decision) {
            setSelectedItemId(id);
            return;
          }
        }
        // Wrap around
        for (let i = 0; i < currentIdx; i++) {
          const id = orderedIds[i];
          if (!currentDecisions.get(id)?.decision) {
            setSelectedItemId(id);
            return;
          }
        }
        // All decided — stay on current or go to overview
      });
    },
    [orderedIds]
  );

  // --- Debounced auto-save (triggers after each decision, debounced 800ms) ---
  const triggerAutoSave = useCallback(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      saveTimerRef.current = null;
      if (!isMountedRef.current) return;

      setIsSaving(true);
      try {
        // TODO: Replace with real GraphQL mutation
        await new Promise((resolve) => setTimeout(resolve, 300));
        if (!isMountedRef.current) return;

        const now = new Date();
        setLastSavedAt(now.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }));
      } finally {
        if (isMountedRef.current) {
          setIsSaving(false);
        }
      }
    }, 800);
  }, []);

  // --- Actions ---
  const makeDecision = useCallback(
    (decision: CourseEvaluatorReviewDecision) => {
      if (!selectedItemId) return;
      const previous = decisions.get(selectedItemId) ?? { decision: null };
      setUndoEntry({ itemId: selectedItemId, previous });
      setDecisions((prev) => {
        const next = new Map(prev);
        next.set(selectedItemId, { decision });
        return next;
      });

      // Non-blocking auto-advance
      advanceToNextUndecided(selectedItemId);

      // Auto-save after decision (debounced)
      triggerAutoSave();
    },
    [selectedItemId, decisions, advanceToNextUndecided, triggerAutoSave]
  );

  const handleAccept = useCallback(() => makeDecision("accepted"), [makeDecision]);
  const handleReject = useCallback(() => makeDecision("rejected"), [makeDecision]);

  const handleUndo = useCallback(() => {
    if (!undoEntry) return;
    setDecisions((prev) => {
      const next = new Map(prev);
      if (undoEntry.previous.decision === null) {
        next.delete(undoEntry.itemId);
      } else {
        next.set(undoEntry.itemId, undoEntry.previous);
      }
      return next;
    });
    setSelectedItemId(undoEntry.itemId);
    setUndoEntry(null);
  }, [undoEntry]);

  const handleNavigateNext = useCallback(() => {
    if (!selectedItemId) {
      if (orderedIds.length > 0) setSelectedItemId(orderedIds[0]);
      return;
    }
    const idx = orderedIds.indexOf(selectedItemId);
    if (idx < orderedIds.length - 1) setSelectedItemId(orderedIds[idx + 1]);
  }, [selectedItemId, orderedIds]);

  const handleNavigatePrev = useCallback(() => {
    if (!selectedItemId) return;
    const idx = orderedIds.indexOf(selectedItemId);
    if (idx > 0) setSelectedItemId(orderedIds[idx - 1]);
    else setSelectedItemId(null);
  }, [selectedItemId, orderedIds]);

  const toggleSection = (sectionId: string) => {
    setCollapsedSectionIds((previous) => {
      const next = new Set(previous);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  return (
    <ReviewPanel
      review={liveReview}
      activityDetails={activityDetails}
      selectedItemId={selectedItemId}
      selectedReviewItem={selectedSidebarItem}
      selectedDecision={selectedDecision}
      filterMode={filterMode}
      collapsedSectionIds={collapsedSectionIds}
      reviewedProgress={reviewedProgress}
      getDecision={getDecision}
      lastSavedAt={lastSavedAt}
      isSaving={isSaving}
      onExitReview={onExitReview}
      onSelectItem={setSelectedItemId}
      onToggleSection={toggleSection}
      onFilterModeChange={setFilterMode}
      onStartReviewing={() => {
        // Select first undecided item
        const firstUndecided = orderedIds.find((id) => !decisions.get(id)?.decision);
        if (firstUndecided) setSelectedItemId(firstUndecided);
      }}
      onAccept={handleAccept}
      onReject={handleReject}
      onUndo={undoEntry ? handleUndo : undefined}
      onNavigateNext={handleNavigateNext}
      onNavigatePrev={handleNavigatePrev}
    />
  );
}

export default CourseEvaluatorReviewContainer;
